/**
 * Back button = "close what's on top", not "leave the app".
 *
 * Every overlay (dialog, sheet, confirm) pushes a history entry as it opens.
 * A back press — phone hardware button, Android gesture, or the browser arrow
 * on a PC — pops that entry and we close the top overlay instead of unloading
 * the page. Only when nothing is open does the root handler get a say, and
 * that one asks for a second press before letting go.
 *
 * Closing an overlay the normal way (X, Escape, Save) removes our entry again
 * with `history.go(-n)`. Browsers disagree on whether that fires one popstate
 * or several, so the handler simply ignores anything that arrives in the few
 * hundred milliseconds after our own call — no human presses back that fast.
 */

const stack = [];
let listening = false;
let skipUntil = 0;
let rootBackHandler = null;

const handlePop = () => {
  if (Date.now() < skipUntil) return;
  const top = stack.pop();
  if (top) {
    try { top.close(); } catch { /* the owner unmounted first */ }
    return;
  }
  if (rootBackHandler) rootBackHandler();
};

const ensureListening = () => {
  if (listening || typeof window === 'undefined') return;
  window.addEventListener('popstate', handlePop);
  listening = true;
};

export function pushOverlay(id, close) {
  if (typeof window === 'undefined') return;
  ensureListening();
  stack.push({ id, close });
  window.history.pushState({ __overlay: id }, '');
}

/** Overlay closed by the app itself — take our history entry back with it. */
export function dropOverlay(id) {
  if (typeof window === 'undefined') return;
  const idx = stack.findIndex((o) => o.id === id);
  if (idx === -1) return;
  const removed = stack.length - idx;
  stack.splice(idx, removed);
  skipUntil = Date.now() + 400;
  window.history.go(-removed);
}

export function overlayCount() {
  return stack.length;
}

export function setRootBackHandler(fn) {
  ensureListening();
  rootBackHandler = fn;
}
